// src/components/AudienceSelector.tsx

import { Input, Select, Typography } from "antd";
import type React from "react";

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

interface AudienceSelectorProps {
  lessonPlanTargetAudience: string;
  customLessonPlanTargetAudience: string;
  setLessonPlanTargetAudience: (value: string) => void;
  setCustomLessonPlanTargetAudience: (value: string) => void;
  isLoadingDocumentInfo?: boolean;
  isProcessingDocument: boolean;
}

const AudienceSelector: React.FC<AudienceSelectorProps> = ({
  lessonPlanTargetAudience,
  customLessonPlanTargetAudience,
  setLessonPlanTargetAudience,
  setCustomLessonPlanTargetAudience,
  isLoadingDocumentInfo,
  isProcessingDocument,
}) => {
  return (
    <div className="mt-4">
      <Text strong>Lesson Plan Target Audience:</Text>
      <Select
        style={{ width: "100%" }}
        value={lessonPlanTargetAudience}
        onChange={(value: string) => {
          setLessonPlanTargetAudience(value);
          if (value !== "custom") {
            // If switching away from custom, clear the custom field
            setCustomLessonPlanTargetAudience("");
          }
        }}
        disabled={isLoadingDocumentInfo || isProcessingDocument}
        className="mt-2"
      >
        <Option value="elementary">
          American elementary school children typically aged between 6 and 10, acquiring basic
          literacy, numeracy, and foundational problem-solving skills.
        </Option>
        <Option value="middle">
          American middle school students generally aged between 11 and 13, transitioning from
          concrete to more abstract learning while developing greater independence and critical
          thinking.
        </Option>
        <Option value="high">
          American high school students between the ages of 14 and 18, broadening their intellectual
          horizons, honing analytical skills, and preparing for post-secondary education or the
          workforce.
        </Option>
        <Option value="college">
          American college or university undergraduates, often between 18 and 22, diving deeper into
          their chosen fields while cultivating critical inquiry, research abilities, and career
          aspirations.
        </Option>
        <Option value="graduate">
          American graduate-level learners, often in their mid-20s or older, engaged in advanced
          study and research within specialized disciplines, seeking expertise and scholarly
          contributions.
        </Option>
        <Option value="adult">
          Adult learners of varying ages and backgrounds, participating in professional development
          or personal enrichment, motivated by self-directed goals and practical application of new
          knowledge.
        </Option>
        <Option value="custom">Custom Audience (provide your own description)</Option>
      </Select>

      {lessonPlanTargetAudience === "custom" && (
        <div className="mt-4">
          <Text strong>Custom Audience Description:</Text>
          <TextArea
            value={customLessonPlanTargetAudience}
            onChange={(e) => setCustomLessonPlanTargetAudience(e.target.value)}
            maxLength={300}
            placeholder="Describe your custom target audience (up to 300 characters)"
            rows={4}
          />
        </div>
      )}
    </div>
  );
};

export default AudienceSelector;
